import {
  describeSshPublicKey,
  getSshPublicKeyError,
  MAX_SSH_PUBLIC_KEYS,
  MAX_SSH_PUBLIC_KEYS_MESSAGE,
  validateSshPublicKey,
} from '@steerfi/shared';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { useId, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { focusAfterRemoval } from '@/hooks/focus-after-removal';

interface SshPublicKeysEditorProps {
  value: string[];
  onChange: (keys: string[]) => void;
  disabled?: boolean;
  /** Id for the "SSH Public Keys" heading; used by the caller's `aria-describedby`/`aria-labelledby`. */
  id?: string | undefined;
  /**
   * Index of a saved key that fails validation (e.g. a key that was valid when
   * added but no longer meets a raised minimum strength). That key's Remove
   * button is marked invalid and linked to `errorId`, since removing it is the
   * only fix. Never defaults to key 0 — an unrelated error must not point at
   * a destructive control by guesswork.
   */
  invalidKeyIndex?: number | null | undefined;
  errorId?: string | undefined;
}

/**
 * Truncates the base64 body of a key for display. Keys are far too long to show
 * in full, and the head and tail together are enough for a human to tell two
 * keys apart.
 */
const previewKeyBody = (key: string): string => {
  const body = key.split(/\s+/)[1] ?? '';
  if (body.length <= 28) return body;
  return `${body.slice(0, 16)}…${body.slice(-8)}`;
};

/**
 * Compares two entries by type and key body only, so the same key re-added with
 * a different comment still counts as a duplicate. Mirrors the de-duplication
 * the API performs on save.
 */
const keyIdentity = (key: string): string => {
  const parts = key.trim().split(/\s+/);
  return parts.length >= 2 ? `${parts[0]} ${parts[1]}` : key.trim();
};

/**
 * Read-only one-line summary of a key: its derived type and size, plus its
 * comment (falling back to a truncated key body when it has none). Used for the
 * non-editing user detail view.
 */
export const SshPublicKeySummary: React.FC<{ sshKey: string }> = ({ sshKey }) => {
  const result = validateSshPublicKey(sshKey);
  return (
    <>
      {result.info ? (
        <Badge variant="secondary">{describeSshPublicKey(result.info)}</Badge>
      ) : (
        <Badge variant="destructive">Invalid</Badge>
      )}
      <span className="font-mono text-xs truncate">
        {result.info?.comment ?? previewKeyBody(sshKey)}
      </span>
    </>
  );
};

/**
 * Add/view/remove editor for a user's authorized SSH public keys.
 *
 * Keys are opaque blobs that are pasted rather than hand-edited, so entries are
 * append-and-remove: existing keys render read-only with their derived type and
 * size, and new keys are validated before being added to the list. The same
 * validation runs again server-side on save.
 */
export const SshPublicKeysEditor: React.FC<SshPublicKeysEditorProps> = ({
  value,
  onChange,
  disabled,
  id,
  invalidKeyIndex,
  errorId,
}) => {
  const generatedId = useId();
  const headingId = id ?? generatedId;
  const draftErrorId = `${headingId}-draft-error`;
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);
  const rowIds = useRef<string[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  while (rowIds.current.length < value.length) {
    rowIds.current.push(crypto.randomUUID());
  }

  const startAdding = () => {
    setDraft('');
    setDraftError(null);
    setIsAdding(true);
  };

  const cancelAdding = () => {
    setDraft('');
    setDraftError(null);
    setIsAdding(false);
  };

  const atLimit = value.length >= MAX_SSH_PUBLIC_KEYS;

  const commitDraft = () => {
    const key = draft.trim().replace(/\s+/g, ' ');

    if (atLimit) {
      setDraftError(MAX_SSH_PUBLIC_KEYS_MESSAGE);
      return;
    }

    const error = getSshPublicKeyError(key);
    if (error) {
      setDraftError(error);
      return;
    }
    if (value.some((existing) => keyIdentity(existing) === keyIdentity(key))) {
      setDraftError('This key has already been added');
      return;
    }

    onChange([...value, key]);
    setDraft('');
    setDraftError(null);
    setIsAdding(false);
  };

  const removeKey = (index: number) => {
    focusAfterRemoval(rootRef.current, rowIds.current, index, () => {
      rowIds.current.splice(index, 1);
      onChange(value.filter((_, i) => i !== index));
    });
  };

  return (
    <div ref={rootRef} className="space-y-2">
      {/* A heading, not a <label>: its target (the Remove button for an invalid
          key) must stay a deliberate click, not something a label activates. */}
      <span id={headingId} className="text-sm font-medium">
        SSH Public Keys
      </span>

      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No SSH public keys.</p>
      ) : (
        <fieldset
          className="space-y-1.5 border-0 p-0 m-0"
          aria-labelledby={headingId}
          aria-describedby={invalidKeyIndex != null && errorId ? errorId : undefined}
        >
          {value.map((key, index) => {
            const result = validateSshPublicKey(key);
            const comment = result.info?.comment;
            const isInvalidKey = invalidKeyIndex === index;
            return (
              <div
                key={rowIds.current[index]}
                className="flex items-start gap-2 p-2 bg-muted/50 rounded-md"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    {result.info ? (
                      <Badge variant="secondary">{describeSshPublicKey(result.info)}</Badge>
                    ) : (
                      <Badge variant="destructive">Invalid</Badge>
                    )}
                    {comment && <span className="text-xs truncate">{comment}</span>}
                  </div>
                  <p className="font-mono text-xs text-muted-foreground truncate">
                    {previewKeyBody(key)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove SSH public key ${index + 1}`}
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  disabled={disabled}
                  data-remove-row={rowIds.current[index]}
                  onClick={() => removeKey(index)}
                  aria-invalid={isInvalidKey || undefined}
                  aria-describedby={isInvalidKey && errorId ? errorId : undefined}
                >
                  <IconTrash size={13} aria-hidden />
                </Button>
              </div>
            );
          })}
        </fieldset>
      )}

      {isAdding ? (
        <div className="space-y-1.5">
          <Textarea
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDraftError(null);
            }}
            placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAA... user@host"
            rows={3}
            className="font-mono text-xs"
            disabled={disabled}
            aria-invalid={draftError ? true : undefined}
            aria-describedby={draftError ? draftErrorId : undefined}
            aria-label="SSH public key"
            // Focus target after the last key is removed: Add SSH key is not
            // rendered while this form is open.
            data-add-row=""
          />
          <p className={draftError ? 'text-xs text-destructive' : 'hidden'} id={draftErrorId}>
            {draftError}
          </p>
          <div className="flex gap-1.5">
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              disabled={disabled || draft.trim() === ''}
              onClick={commitDraft}
            >
              Add
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={disabled}
              onClick={cancelAdding}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1"
            disabled={disabled || atLimit}
            data-add-row=""
            onClick={startAdding}
          >
            <IconPlus size={12} />
            Add SSH key
          </Button>
          {atLimit && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              {MAX_SSH_PUBLIC_KEYS_MESSAGE}.
            </p>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Paste the contents of a public key file (e.g. <code>id_ed25519.pub</code>). Supported: RSA
        (2048 bits or more), ECDSA (nistp256 or better), and ed25519. Keys are optional — SFTP users
        can still authenticate with a password.
      </p>
    </div>
  );
};
